// File: frontend/src/pages/Leads/LeadDetailPage.jsx
// Lead detail page - Full implementation

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  StarIcon,
  PencilIcon,
  PlusIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';
import ActivityForm from '../../components/Activities/ActivityForm';
import CreateLeadModal from '../../components/Leads/CreateLeadModal';
import AIEnrichmentPanel from '../../components/AI/AIEnrichmentPanel';
import WhatsAppConversationPanel from '../../components/WhatsApp/WhatsAppConversationPanel';
import WhatsappCockpit from '../../components/Pipeline/WhatsappCockpit';
import EmailComposeModal from '../../components/Email/EmailComposeModal';
import ScheduleMeetingModal from '../../components/Calendar/ScheduleMeetingModal';
import { formatLeadScore } from '../../utils/leadScore';

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityType, setActivityType] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/leads/${id}?include_activities=true`);
        
        if (response.data.success) {
          setLead(response.data.data);
          setActivities(response.data.data.activities || []);
        } else {
          setError('Lead não encontrado');
        }
      } catch (error) {
        console.error('Error fetching lead details:', error);
        setError('Erro ao carregar detalhes do lead');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLeadDetails();
    }
  }, [id]);

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'qualified': 'bg-green-100 text-green-800',
      'nurturing': 'bg-purple-100 text-purple-800',
      'converted': 'bg-green-100 text-green-800',
      'lost': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getLifecycleColor = (stage) => {
    const colors = {
      'subscriber': 'bg-gray-100 text-gray-800',
      'lead': 'bg-blue-100 text-blue-800',
      'marketing_qualified_lead': 'bg-green-100 text-green-800',
      'sales_qualified_lead': 'bg-purple-100 text-purple-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLeadSource = (source) => {
    const sources = {
      'website': 'Website',
      'rd_station': 'RD Station',
      'social_media': 'Redes Sociais',
      'referral': 'Indicação',
      'cold_outreach': 'Prospecção',
      'event': 'Evento'
    };
    return sources[source] || source;
  };

  const formatLifecycleStage = (stage) => {
    const stages = {
      'subscriber': 'Assinante',
      'lead': 'Lead',
      'marketing_qualified_lead': 'Lead Qualificado (Marketing)',
      'sales_qualified_lead': 'Lead Qualificado (Vendas)'
    };
    return stages[stage] || stage;
  };

  const formatLeadStatus = (status) => {
    const statuses = {
      'new': 'Novo',
      'contacted': 'Contatado',
      'qualified': 'Qualificado',
      'nurturing': 'Nutrição',
      'converted': 'Convertido',
      'lost': 'Perdido'
    };
    return statuses[status] || status;
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'email':
        setShowEmailModal(true);
        break;
      case 'call':
        if (lead.phone) {
          const digits = lead.phone.replace(/\D/g, '');
          window.location.href = `tel:${digits}`;
        } else {
          toast.error('Telefone não disponível para este lead');
        }
        break;
      case 'meeting':
        setShowMeetingModal(true);
        break;
      case 'whatsapp':
        if (lead.phone) {
          const phoneNumber = lead.phone.replace(/\D/g, '');
          window.open(`https://wa.me/${phoneNumber}`, '_blank');
          api.post(`/leads/${id}/whatsapp/log-personal`).catch(() => {});
          // Refresh activities list after logging
          setTimeout(() => {
            api.get(`/leads/${id}?include_activities=true`).then((r) => {
              if (r.data.success) setActivities(r.data.data.activities || []);
            }).catch(() => {});
          }, 1500);
        } else {
          toast.error('Telefone não disponível para este lead');
        }
        break;
      case 'convert':
        setShowConversionModal(true);
        break;
      default:
        toast.info('Funcionalidade em desenvolvimento');
    }
  };

  const handleConvertLead = async () => {
    try {
      const response = await api.post(`/leads/${id}/convert`);
      if (response.data.success) {
        toast.success('Lead convertido para Oportunidade!');
        navigate('/pipeline');
      }
    } catch (error) {
      console.error('Error converting lead:', error);
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Erro ao converter lead');
    } finally {
      setShowConversionModal(false);
    }
  };

  const handleEditLead = () => {
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
            <div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erro</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/leads')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Voltar aos Leads
          </button>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">Lead não encontrado</h1>
          <button
            onClick={() => navigate('/leads')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Voltar aos Leads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/leads')}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {lead.full_name}
              </h1>
              <p className="text-gray-600">Lead ID: {lead.id}</p>
            </div>
          </div>
          <button
            onClick={handleEditLead}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <PencilIcon className="h-4 w-4" />
            <span>Editar</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações do Lead</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{lead.full_name}</p>
                  <p className="text-sm text-gray-500">Nome completo</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{lead.email}</p>
                  <p className="text-sm text-gray-500">Email</p>
                </div>
              </div>

              {lead.phone && (
                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{lead.phone}</p>
                    <p className="text-sm text-gray-500">Telefone</p>
                  </div>
                </div>
              )}

              {lead.company && (
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{lead.company.name}</p>
                    <p className="text-sm text-gray-500">Empresa</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(lead.created_at)}
                  </p>
                  <p className="text-sm text-gray-500">Data de criação</p>
                </div>
              </div>

              {lead.owner && (
                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{lead.owner.name}</p>
                    <p className="text-sm text-gray-500">Responsável</p>
                  </div>
                </div>
              )}
            </div>

            {lead.notes && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Observações</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* AI Enrichment Panel */}
          <AIEnrichmentPanel contact={lead} />

          {/* Activities */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Atividades</h2>
            
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity.id} className="border-l-4 border-blue-200 pl-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">{activity.subject}</h3>
                      <span className="text-xs text-gray-500">
                        {formatDate(activity.activity_date)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                    {activity.user_name && (
                      <p className="text-xs text-gray-500 mt-1">por {activity.user_name}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Nenhuma atividade registrada</p>
            )}
          </div>

          {/* WhatsApp Conversation Thread */}
          <WhatsAppConversationPanel phone={lead.phone} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Cards */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500 mb-1">Estado do Lead</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLifecycleColor(lead.lifecycle_stage)}`}>
                  {formatLifecycleStage(lead.lifecycle_stage)}
                </span>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.lead_status)}`}>
                  {formatLeadStatus(lead.lead_status)}
                </span>
              </div>

              {lead.lead_source && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Fonte</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatLeadSource(lead.lead_source)}
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-1">Pontuação</p>
                <div className="flex items-center space-x-1">
                  <StarIcon className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {formatLeadScore(lead.lead_score)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
            
            <div className="space-y-2">
              <button 
                onClick={() => handleQuickAction('email')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2"
              >
                <EnvelopeIcon className="h-4 w-4" />
                <span>Enviar Email</span>
              </button>
              <button 
                onClick={() => handleQuickAction('call')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2"
              >
                <PhoneIcon className="h-4 w-4" />
                <span>Fazer Chamada</span>
              </button>
              <button 
                onClick={() => handleQuickAction('meeting')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2"
              >
                <CalendarIcon className="h-4 w-4" />
                <span>Agendar Reunião</span>
              </button>
              {lead.phone && (
                <button 
                  onClick={() => handleQuickAction('whatsapp')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2"
                >
                  <DevicePhoneMobileIcon className="h-4 w-4" />
                  <span>WhatsApp pessoal</span>
                </button>
              )}
              {/* Only show convert button if lead is not already converted */}
              {lead.lead_status !== 'converted' && lead.lifecycle_stage !== 'customer' && lead.lifecycle_stage !== 'opportunity' ? (
                <button 
                  onClick={() => handleQuickAction('convert')}
                  className="w-full text-left px-3 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md flex items-center space-x-2"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>Converter Lead</span>
                </button>
              ) : (
                <div className="w-full text-center px-3 py-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md flex items-center justify-center space-x-2">
                  <UserIcon className="h-4 w-4" />
                  <span>Lead Convertido</span>
                </div>
              )}
            </div>
          </div>

          {/* WhatsApp HSM Cockpit */}
          <WhatsappCockpit entityPath="leads" entityId={lead.id} />
        </div>
      </div>

      {/* Activity Form Modal */}
      {showActivityModal && (
        <ActivityForm
          isOpen={showActivityModal}
          onClose={() => {
            setShowActivityModal(false);
            setActivityType(null);
          }}
          contactId={null}
          companyId={lead.company?.id || null}
          leadId={lead.id}
          activity={activityType ? { type: activityType, lead_id: lead.id, subject: `${activityType === 'email' ? 'Email para' : activityType === 'call' ? 'Ligação para' : 'Reunião com'} ${lead.full_name}` } : null}
          onSuccess={() => {
            setShowActivityModal(false);
            setActivityType(null);
            // Refresh activities
            const fetchLeadDetails = async () => {
              try {
                const response = await api.get(`/leads/${id}?include_activities=true`);
                if (response.data.success) {
                  setActivities(response.data.data.activities || []);
                }
              } catch (error) {
                console.error('Error refreshing activities:', error);
              }
            };
            fetchLeadDetails();
          }}
        />
      )}

      {/* Lead Conversion Modal */}
      {showConversionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Converter Lead em Oportunidade
              </h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja converter o lead <strong>{lead.full_name}</strong> em Oportunidade?
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-2">O que acontecerá:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Será criada uma Oportunidade no pipeline de vendas</li>
                  <li>• O lead passa para a etapa Oportunidade</li>
                  <li>• Todas as atividades serão mantidas</li>
                  <li>• Você será levado ao pipeline</li>
                </ul>
              </div>
              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => setShowConversionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConvertLead}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Converter Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lead Modal */}
      <CreateLeadModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        lead={lead}
        onSuccess={() => {
          // Refresh lead data after successful edit
          const fetchLeadDetails = async () => {
            try {
              const response = await api.get(`/leads/${id}?include_activities=true`);
              if (response.data.success) {
                setLead(response.data.data);
                setActivities(response.data.data.activities || []);
              }
            } catch (error) {
              console.error('Error refreshing lead details:', error);
            }
          };
          fetchLeadDetails();
          setShowEditModal(false);
        }}
      />

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        contact={lead}
        onSendSuccess={() => {
          // Refresh activities after email is sent
          api.get(`/leads/${id}?include_activities=true`).then((r) => {
            if (r.data.success) setActivities(r.data.data.activities || []);
          }).catch(() => {});
        }}
      />

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        contact={lead}
        onScheduleSuccess={() => {
          // Refresh activities after meeting is scheduled
          api.get(`/leads/${id}?include_activities=true`).then((r) => {
            if (r.data.success) setActivities(r.data.data.activities || []);
          }).catch(() => {});
        }}
      />
    </div>
  );
};

export default LeadDetailPage;